<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from "vue";
import {
  isJust,
  isNothing,
  just,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import {
  buildCalendarHeatmap,
  contributionLevel,
  formatChronicleDay,
  formatChronicleWhen,
  formatEntryPaths,
  formatWordsDiff,
  listChronicleEntries,
  platformLabel,
  type CalendarDayCell,
  type ChronicleEntry,
} from "../lib/chronicle";
import {
  buildChroniclePdfBook,
  chroniclePdfDefaultName,
} from "../lib/chroniclePdf";
import { pdfBookToNativeJson } from "../export/pdfBook";
import { isTauri } from "../lib/runtime";
import { saveBookPdfNative } from "../lib/tauri/export";
import {
  dismissExportToast,
  reportSavedExport,
  reportExportFailed,
} from "../composables/useExportToast";
import { formatExportFailure } from "../composables/useExportToast.update";
import { useAnchoredFloating } from "../composables/useAnchoredFloating";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import ZwButton from "./ui/ZwButton.vue";

const props = defineProps<{
  rootPath: Maybe<string>;
  projectTitle: string;
  /** When true, load (or reload) chronicle entries. */
  active: boolean;
}>();

type ViewMode = "heatmap" | "table";

/** Weekday rows: Sun=0 … Sat=6. Labels only on Mon/Wed/Fri like GitHub. */
const WEEKDAY_LABELS = ["", "Mon", "", "Wed", "", "Fri", ""];

const session = useEditorSessionStore();
const mode = ref<ViewMode>("heatmap");
const loading = ref(false);
const exporting = ref(false);
const error = ref<Maybe<string>>(nothing);
const entries = ref<ChronicleEntry[]>([]);
const exportError = ref<Maybe<string>>(nothing);

const calendar = computed(() =>
  buildCalendarHeatmap(entries.value, new Date(), 53, session.chapters),
);
const tableRows = computed(() =>
  [...entries.value].sort((a, b) => b.timestamp.localeCompare(a.timestamp)),
);

type DayHover = {
  cell: CalendarDayCell;
  anchor: HTMLElement;
};

const dayHover = ref<Maybe<DayHover>>(nothing);
const dayHoverEl = useTemplateRef<HTMLElement>("dayHoverEl");
// Floating UI keeps the hover inside the window (flips and slides at the
// edges) instead of centring it on the day and running off the side.
const { floatingStyle: dayHoverStyle } = useAnchoredFloating(
  computed(() =>
    isJust(dayHover.value) ? just(dayHover.value.value.anchor) : nothing,
  ),
  computed(() => toMaybe(dayHoverEl.value)),
  computed(() => isJust(dayHover.value)),
);

function filesForEntry(entry: ChronicleEntry): string {
  return formatEntryPaths(entry, session.chapters);
}

function cellFiles(cell: CalendarDayCell): string[] {
  if (cell.pathLabels.length > 0) return cell.pathLabels;
  return cell.paths;
}

function showDayHover(cell: Maybe<CalendarDayCell>, event: MouseEvent): void {
  if (isNothing(cell)) {
    dayHover.value = nothing;
    return;
  }
  if (event.currentTarget instanceof HTMLElement === false) return;
  dayHover.value = just({ cell: cell.value, anchor: event.currentTarget });
}

function hideDayHover(): void {
  dayHover.value = nothing;
}

watch(mode, () => {
  dayHover.value = nothing;
});

watch(
  () => [props.active, props.rootPath] as const,
  ([active, root]) => {
    if (active === false) return;
    if (isNothing(root)) return;
    void load(root.value);
  },
  { immediate: true },
);

async function load(root: string): Promise<void> {
  loading.value = true;
  error.value = nothing;
  try {
    entries.value = await listChronicleEntries(root);
  } catch (err) {
    console.error("[chronicle] failed to load", err);
    error.value = just("Couldn't load the writing chronicle right now.");
    entries.value = [];
  } finally {
    loading.value = false;
  }
}

function cellClass(cell: Maybe<CalendarDayCell>): string {
  if (isNothing(cell)) return "chronicle-day is-pad";
  const level = contributionLevel(
    cell.value.wordsDiff,
    calendar.value.maxAbsDiff,
  );
  if (level === 0) return "chronicle-day level-0";
  const sign = cell.value.wordsDiff > 0 ? "pos" : "neg";
  return `chronicle-day level-${sign}-${level}`;
}

async function exportPdf(): Promise<void> {
  if (isTauri() === false || exporting.value === true) return;
  exporting.value = true;
  exportError.value = nothing;
  dismissExportToast();
  try {
    const book = buildChroniclePdfBook(
      entries.value,
      props.projectTitle,
      session.chapters,
    );
    reportSavedExport(
      "PDF",
      await saveBookPdfNative(
        pdfBookToNativeJson(book),
        chroniclePdfDefaultName(props.projectTitle),
      ),
    );
  } catch (err) {
    console.error("[chronicle] PDF export failed", err);
    const message = formatExportFailure(
      "Couldn't export the chronicle PDF.",
      err,
    );
    exportError.value = just(message);
    reportExportFailed(message);
  } finally {
    exporting.value = false;
  }
}
</script>

<template>
  <section
    class="project-settings-group chronicle-section"
    aria-labelledby="settings-chronicle-heading"
  >
    <h3 id="settings-chronicle-heading" class="project-settings-subhead">
      Writing history
    </h3>
    <div class="chronicle-toolbar">
      <div class="chronicle-toolbar-left">
        <p class="project-settings-hint">
          View a record of your writing activity over the last year.
        </p>
      </div>
      <div class="chronicle-toolbar-right">
        <ZwButton
          v-if="isTauri()"
          size="sm"
          variant="primary"
          :disabled="loading || exporting || isNothing(rootPath)"
          @click="exportPdf"
        >
          {{ exporting ? "Exporting…" : "Export PDF" }}
        </ZwButton>
      </div>
    </div>
    <!-- <div v-if="SHOW_VIEW_TOGGLE || isTauri()" class="chronicle-toolbar">
      <div
        v-if="SHOW_VIEW_TOGGLE"
        class="chronicle-mode"
        role="tablist"
        aria-label="Chronicle view"
      >
        <button
          type="button"
          role="tab"
          class="chronicle-mode-btn"
          :class="{ active: mode === 'heatmap' }"
          :aria-selected="mode === 'heatmap'"
          @click="mode = 'heatmap'"
        >
          Chart
        </button>
        <button
          type="button"
          role="tab"
          class="chronicle-mode-btn"
          :class="{ active: mode === 'table' }"
          :aria-selected="mode === 'table'"
          @click="mode = 'table'"
        >
          Table
        </button>
      </div>
    </div> -->

    <p v-if="isJust(exportError)" class="chronicle-error" role="alert">
      {{ exportError.value }}
    </p>

    <div v-if="loading" class="chronicle-status">Loading…</div>
    <div v-else-if="isJust(error)" class="chronicle-error" role="alert">
      {{ error.value }}
    </div>
    <div v-else-if="isNothing(rootPath)" class="chronicle-status">
      Open a project to view its chronicle.
    </div>
    <div v-else-if="entries.length === 0" class="chronicle-status">
      Nothing here yet. Days will fill in as you write and save.
    </div>

    <template v-else>
      <div
        v-if="mode === 'heatmap'"
        class="chronicle-calendar-wrap"
        @mouseleave="hideDayHover"
      >
        <div
          class="chronicle-calendar"
          role="img"
          :aria-label="`Writing activity from ${calendar.startDay} to ${calendar.endDay}`"
          :style="{ '--week-count': String(calendar.weeks.length) }"
        >
          <div class="chronicle-months" aria-hidden="true">
            <span class="chronicle-weekday-spacer" />
            <div class="chronicle-months-row">
              <span
                v-for="m in calendar.monthLabels"
                :key="`m-${m.weekIndex}`"
                class="chronicle-month-label"
                :style="{ '--week-i': String(m.weekIndex) }"
              >
                {{ m.label }}
              </span>
            </div>
          </div>

          <div class="chronicle-body">
            <div class="chronicle-weekdays" aria-hidden="true">
              <span
                v-for="(label, i) in WEEKDAY_LABELS"
                :key="`wd-${i}`"
                class="chronicle-weekday"
              >
                {{ label }}
              </span>
            </div>
            <div class="chronicle-weeks">
              <div
                v-for="(week, wi) in calendar.weeks"
                :key="`w-${wi}`"
                class="chronicle-week"
              >
                <span
                  v-for="(cell, di) in week"
                  :key="`d-${wi}-${di}`"
                  class="chronicle-day"
                  :class="cellClass(toMaybe(cell))"
                  @mouseenter="showDayHover(toMaybe(cell), $event)"
                />
              </div>
            </div>
          </div>

          <div class="chronicle-legend" aria-hidden="true">
            <span class="chronicle-legend-scale">
              <span class="chronicle-legend-caption">More deletions</span>
              <span class="chronicle-day level-neg-4" />
              <span class="chronicle-day level-neg-3" />
              <span class="chronicle-day level-neg-2" />
              <span class="chronicle-day level-neg-1" />
              <span class="chronicle-day level-0" />
              <span class="chronicle-day level-pos-1" />
              <span class="chronicle-day level-pos-2" />
              <span class="chronicle-day level-pos-3" />
              <span class="chronicle-day level-pos-4" />
              <span class="chronicle-legend-caption">More additions</span>
            </span>
          </div>
        </div>

        <div
          v-if="isJust(dayHover)"
          ref="dayHoverEl"
          class="chronicle-day-hover"
          role="tooltip"
          :style="dayHoverStyle"
        >
          <p class="chronicle-day-hover-when">
            {{ formatChronicleDay(dayHover.value.cell.day) }}
          </p>
          <p
            v-if="dayHover.value.cell.eventCount === 0"
            class="chronicle-day-hover-meta"
          >
            No writing recorded
          </p>
          <template v-else>
            <p class="chronicle-day-hover-meta">
              {{ formatWordsDiff(dayHover.value.cell.wordsDiff) }} words
            </p>
            <ul
              v-if="cellFiles(dayHover.value.cell).length > 0"
              class="chronicle-day-hover-files"
            >
              <li
                v-for="(file, fi) in cellFiles(dayHover.value.cell)"
                :key="`${file}-${fi}`"
              >
                {{ file }}
              </li>
            </ul>
            <p v-else class="chronicle-day-hover-meta">No files recorded</p>
          </template>
        </div>
      </div>

      <div v-else class="chronicle-table-wrap zw-scroll">
        <table class="chronicle-table">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">Editor</th>
              <th scope="col">Message</th>
              <th scope="col" class="num">Words</th>
              <th scope="col">Files</th>
              <th scope="col">Platform</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(entry, i) in tableRows"
              :key="`${entry.seq}-${entry.timestamp}-${i}`"
            >
              <td>{{ formatChronicleWhen(entry.timestamp) }}</td>
              <td>
                {{
                  (entry.author?.trim() ?? "").length > 0
                    ? entry.author?.trim()
                    : "-"
                }}
              </td>
              <td>
                {{
                  typeof entry.message === "string" && entry.message.length > 0
                    ? entry.message
                    : "-"
                }}
              </td>
              <td class="num">{{ formatWordsDiff(entry.wordsDiff ?? 0) }}</td>
              <td :title="(entry.paths ?? []).join(', ')">
                {{ filesForEntry(entry) }}
              </td>
              <td>{{ platformLabel(entry.platform ?? null) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </section>
</template>

<style scoped>
.chronicle-section {
  min-width: 0;
  width: 100%;
}

.chronicle-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
  margin: 0.35rem 0 0.75rem;
}

.chronicle-mode {
  display: inline-flex;
  border: 1px solid var(--line);
  border-radius: 0.3rem;
  overflow: hidden;
  background: var(--bg);
}

.chronicle-mode-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0.35rem 0.7rem;
  font-family: var(--font-ui);
  font-size: 0.82rem;
  color: var(--text-muted);
  cursor: pointer;
}

.chronicle-mode-btn + .chronicle-mode-btn {
  border-left: 1px solid var(--line);
}

.chronicle-mode-btn.active {
  background: var(--bg-raised);
  color: var(--text-strong);
  font-weight: 600;
}

.chronicle-status {
  margin: 0.35rem 0 0;
  font-family: var(--font-ui);
  font-size: 0.88rem;
  line-height: 1.45;
  color: var(--text-muted);
}

.chronicle-error {
  margin: 0.35rem 0 0;
  font-family: var(--font-ui);
  font-size: 0.88rem;
  color: var(--danger);
}

.chronicle-calendar-wrap,
.chronicle-table-wrap {
  margin-top: 0.55rem;
  border: 1px solid var(--line);
  border-radius: 0.35rem;
  background: var(--bg-raised);
}

.chronicle-calendar-wrap {
  position: relative;
  padding: 0.7rem 0.75rem 0.55rem;
  overflow: visible;
}

.chronicle-calendar {
  --week-count: 53;
  --weekday-col: 1.55rem;
  --day-gap: clamp(0.1rem, 0.35vw, 0.18rem);
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  width: 100%;
  font-family: var(--font-ui);
}

.chronicle-months {
  display: grid;
  grid-template-columns: var(--weekday-col) minmax(0, 1fr);
  gap: var(--day-gap);
  align-items: end;
  min-height: 0.9rem;
}

.chronicle-weekday-spacer {
  min-width: 0;
}

.chronicle-months-row {
  position: relative;
  min-width: 0;
  height: 0.9rem;
  /* Labels spill across week columns; clip only the far right edge. */
  overflow: hidden;
}

.chronicle-month-label {
  position: absolute;
  top: 0;
  left: calc(var(--week-i) / var(--week-count) * 100%);
  font-size: 0.68rem;
  color: var(--text-muted);
  line-height: 1;
  white-space: nowrap;
  pointer-events: none;
}

.chronicle-body {
  display: grid;
  grid-template-columns: var(--weekday-col) minmax(0, 1fr);
  gap: var(--day-gap);
  min-width: 0;
}

.chronicle-weekdays {
  display: grid;
  grid-template-rows: repeat(7, minmax(0, 1fr));
  gap: var(--day-gap);
  align-self: stretch;
  font-size: clamp(0.55rem, 1vw, 0.68rem);
  color: var(--text-muted);
  min-height: 0;
}

.chronicle-weekday {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 0.1rem;
  min-height: 0;
}

.chronicle-weeks {
  display: grid;
  grid-template-columns: repeat(var(--week-count), minmax(0, 1fr));
  gap: var(--day-gap);
  min-width: 0;
}

.chronicle-week {
  display: grid;
  grid-template-rows: repeat(7, auto);
  gap: var(--day-gap);
  min-width: 0;
}

.chronicle-day {
  display: block;
  width: 100%;
  aspect-ratio: 1;
  min-width: 0;
  border-radius: 0.12rem;
  border: 1px solid color-mix(in oklch, var(--line) 80%, transparent);
  background: color-mix(in oklch, var(--bg) 88%, var(--text-muted));
}

.chronicle-day.is-pad {
  visibility: hidden;
  border-color: transparent;
  background: transparent;
}

.chronicle-day.level-0 {
  background: color-mix(in oklch, var(--bg) 88%, var(--text-muted));
}

/* Solid cool greens / clear reds. Mixing into the warm page bg was turning
   light greens olive-brown and reading as reddish. */
.chronicle-day.level-pos-1 {
  background: oklch(0.32 0.07 155);
  border-color: oklch(0.38 0.08 155);
}
.chronicle-day.level-pos-2 {
  background: oklch(0.42 0.11 154);
  border-color: oklch(0.48 0.12 154);
}
.chronicle-day.level-pos-3 {
  background: oklch(0.55 0.14 152);
  border-color: oklch(0.6 0.14 152);
}
.chronicle-day.level-pos-4 {
  background: oklch(0.72 0.17 150);
  border-color: oklch(0.76 0.15 150);
}

.chronicle-day.level-neg-1 {
  background: oklch(0.32 0.08 22);
  border-color: oklch(0.38 0.09 22);
}
.chronicle-day.level-neg-2 {
  background: oklch(0.42 0.12 20);
  border-color: oklch(0.48 0.13 20);
}
.chronicle-day.level-neg-3 {
  background: oklch(0.55 0.16 18);
  border-color: oklch(0.6 0.15 18);
}
.chronicle-day.level-neg-4 {
  background: oklch(0.7 0.18 16);
  border-color: oklch(0.74 0.16 16);
}

.chronicle-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 0.3rem;
  padding-top: 0.4rem;
  border-top: 1px solid var(--line);
  font-size: clamp(0.62rem, 1.1vw, 0.72rem);
  color: var(--text-muted);
}

.chronicle-legend-scale {
  display: inline-flex;
  align-items: center;
  gap: 0.18rem;
}

.chronicle-legend-caption {
  margin-inline: 0.15rem;
}

.chronicle-legend-scale .chronicle-day {
  width: 0.65rem;
  height: 0.65rem;
  aspect-ratio: auto;
  flex-shrink: 0;
}

.chronicle-day-hover {
  z-index: 5;
  min-width: 11rem;
  max-width: min(18rem, var(--zw-float-max-width, 70vw));
  padding: 0.55rem 0.65rem;
  border: 1px solid var(--line);
  border-radius: 0.35rem;
  background: var(--bg-raised);
  color: var(--text-strong);
  font-family: var(--font-ui);
  font-size: 0.78rem;
  line-height: 1.35;
  pointer-events: none;
}

.chronicle-day-hover-when {
  margin: 0;
  font-weight: 600;
  color: var(--text-strong);
}

.chronicle-day-hover-meta {
  margin: 0.2rem 0 0;
  color: var(--text-muted);
}

.chronicle-day-hover-files {
  margin: 0.4rem 0 0;
  padding: 0.35rem 0 0;
  border-top: 1px solid var(--line);
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  max-height: 10rem;
  overflow: auto;
  pointer-events: auto;
}

.chronicle-day-hover-files li {
  margin: 0;
  padding: 0;
  color: var(--text-strong);
}

.chronicle-table-wrap {
  max-height: min(52vh, 28rem);
  overflow: auto;
}

.chronicle-table {
  width: 100%;
  border-collapse: collapse;
  font-family: var(--font-ui);
  font-size: 0.78rem;
}

.chronicle-table th,
.chronicle-table td {
  padding: 0.4rem 0.55rem;
  border-bottom: 1px solid var(--line);
  text-align: left;
  vertical-align: top;
}

.chronicle-table th {
  position: sticky;
  top: 0;
  background: var(--bg-raised);
  z-index: 1;
  font-weight: 600;
  color: var(--text-muted);
  white-space: nowrap;
}

.chronicle-table td {
  color: var(--text);
}

.chronicle-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
</style>
